/**
 * QuotePDFTemplate
 *
 * Template compartilhado para geração de PDF de orçamentos.
 * Layout compacto (preferência: 1 página A4).
 */

import { QUOTE_PDF_COMPANY } from './quotePdfConstants'
import './QuotePDFTemplate.css'

const HIDDEN_CONTAINER_STYLE = {
  position: 'absolute',
  left: '-9999px',
  top: 0,
  width: '210mm',
  backgroundColor: '#FFFFFF'
}

function formatMoney(value) {
  return `R$ ${parseFloat(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function formatShortDate(value) {
  if (!value) return null
  return new Date(value).toLocaleDateString('pt-BR')
}

function QuotePDFTemplate({ pdfQuoteData, itemsRef, summaryRef, showDiscountColumn = true }) {
  if (!pdfQuoteData) return null

  const emissionDate = formatShortDate(pdfQuoteData.quoteDate) || formatShortDate(new Date())
  const validityDate = formatShortDate(pdfQuoteData.validityDate)

  return (
    <>
      <div ref={itemsRef} className="order-pdf-container order-pdf-container--compact" style={HIDDEN_CONTAINER_STYLE}>
        <header className="order-pdf-header order-pdf-header--minimal">
          <div className="order-pdf-header-top">
            <div className="order-pdf-brand">
              <span className="order-pdf-brand-name">{QUOTE_PDF_COMPANY.name}</span>
              <span className="order-pdf-brand-meta">CNPJ {QUOTE_PDF_COMPANY.cnpj}</span>
            </div>
            <span className="order-pdf-header-badge">Orçamento</span>
          </div>
          <div className="order-pdf-header-accent" aria-hidden="true" />
        </header>

        <div className="order-pdf-body order-pdf-body--compact">
          <div className="order-pdf-parties">
            <div className="order-pdf-party">
              <h4 className="order-pdf-party-title">Dados do Orçamento</h4>
              <div>
                <p className="order-pdf-party-name">Orçamento #{pdfQuoteData.number || pdfQuoteData.id}</p>
                {emissionDate && (
                  <p className="order-pdf-party-detail">Emissão: {emissionDate}</p>
                )}
                <p className="order-pdf-party-detail">
                  {validityDate ? `Validade: ${validityDate}` : 'Validade não informada'}
                </p>
                {pdfQuoteData.paymentCondition && (
                  <p className="order-pdf-party-cnpj">
                    Condição: {pdfQuoteData.paymentCondition}
                  </p>
                )}
              </div>
            </div>

            {pdfQuoteData.customer && (
              <div className="order-pdf-party order-pdf-party--right">
                <h4 className="order-pdf-party-title">Cliente</h4>
                <div>
                  <p className="order-pdf-party-name">{pdfQuoteData.customer.name || 'N/A'}</p>
                  <p className="order-pdf-party-detail">
                    {pdfQuoteData.customer.cpf_cnpj
                      ? `CNPJ/CPF: ${pdfQuoteData.customer.cpf_cnpj}`
                      : 'CNPJ/CPF não informado'}
                  </p>
                  {pdfQuoteData.customer.email && (
                    <p className="order-pdf-party-cnpj">{pdfQuoteData.customer.email}</p>
                  )}
                  {(pdfQuoteData.customer.phone || pdfQuoteData.customer.cellphone) && (
                    <p className="order-pdf-party-cnpj">
                      {pdfQuoteData.customer.phone || pdfQuoteData.customer.cellphone}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {pdfQuoteData.items?.length > 0 && (
            <div className="order-pdf-section order-pdf-section--compact">
              <table className="order-pdf-table">
                <thead>
                  <tr>
                    <th>Descrição do Produto</th>
                    <th className="order-pdf-table-center">Qtd</th>
                    <th className="order-pdf-table-right">Unitário</th>
                    {showDiscountColumn && <th className="order-pdf-table-right">Desconto</th>}
                    <th className="order-pdf-table-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {pdfQuoteData.items.map((item, idx) => (
                    <tr key={idx} className="order-pdf-table-row">
                      <td>
                        <p className="order-pdf-product-name">{item.product_name || 'Produto não informado'}</p>
                        <p className="order-pdf-product-note">
                          {item.sku && item.sku !== '-' ? `SKU: ${item.sku}` : `Item #${idx + 1}`}
                        </p>
                      </td>
                      <td className="order-pdf-table-center">
                        {parseFloat(item.quantity || 0).toLocaleString('pt-BR', {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 3
                        })}
                      </td>
                      <td className="order-pdf-table-right">
                        {item.unit_price != null ? formatMoney(item.unit_price) : '-'}
                      </td>
                      {showDiscountColumn && (
                        <td className="order-pdf-table-right">
                          {item.discount_percent > 0
                            ? `${parseFloat(item.discount_percent).toFixed(2)}%`
                            : '-'}
                        </td>
                      )}
                      <td className="order-pdf-table-right">
                        {item.total_price != null ? formatMoney(item.total_price) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="order-pdf-summary order-pdf-summary--inline">
            <div className="order-pdf-notes">
              <h4 className="order-pdf-notes-title">Informações</h4>
              <ul className="order-pdf-notes-list">
                {validityDate && <li>• Validade: {validityDate}</li>}
                {pdfQuoteData.paymentCondition && (
                  <li>• Pagamento: {pdfQuoteData.paymentCondition}</li>
                )}
                {pdfQuoteData.customerObservation && (
                  <li>• Observações: {pdfQuoteData.customerObservation}</li>
                )}
                <li>• Produtos sujeitos à disponibilidade de estoque.</li>
              </ul>
            </div>
            <div className="order-pdf-totals">
              <div className="order-pdf-total-row">
                <span>Subtotal</span>
                <span className="order-pdf-total-row-value">{formatMoney(pdfQuoteData.subtotal)}</span>
              </div>
              {pdfQuoteData.discount > 0 && (
                <div className="order-pdf-total-row">
                  <span>Desconto</span>
                  <span className="order-pdf-total-row-value">- {formatMoney(pdfQuoteData.discount)}</span>
                </div>
              )}
              <div className="order-pdf-final-total">
                <p className="order-pdf-final-total-label">Total do Orçamento</p>
                <p className="order-pdf-final-total-value">{formatMoney(pdfQuoteData.total)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Placeholder: resumo já está no documento principal (1 página quando couber). */}
      <div
        ref={summaryRef}
        className="order-pdf-summary-placeholder"
        style={{ ...HIDDEN_CONTAINER_STYLE, width: '1px', height: '1px', overflow: 'hidden', padding: 0 }}
        aria-hidden="true"
      />
    </>
  )
}

export default QuotePDFTemplate
